import { TableName } from "../../../tables"
import { isDefined, round } from "../../../utils/typeUtils"
import { usePaymentTypeHooks } from "../../administration"
import { Perception } from "../../fiscal-documents"
import { Budget, HoardOrder, HoardType, SaleOrder } from "../../sales"
import { Item, Operation } from "../state/types"
import { usePricesHooks } from "./pricesHooks"
import { ItemPriceBreakdownType, ItemPriceOutlineType, PriceOutlineType } from "./types"

export const usePriceOutlineHooks = () => {
    const pricesHooks = usePricesHooks()
    const { getPaymentTypeYield } = usePaymentTypeHooks()

    const calculateItemPriceOutline = (
        item: Partial<Item>,
        customerTypeYield = 0,
        vatTaxYield = 0,
        paymentTypeYield = 0,
    ): ItemPriceOutlineType => {
        const price = item.price!
        const quantity = item.quantity!
        const productId = item.productId!

        const customerAdjustmentUnitAmount = price * customerTypeYield / 100
        const customerUnitSubtotal = price + customerAdjustmentUnitAmount
        
        const productDiscountsYield = item.discounts?.reduce((acc, discount) => acc + discount.yield, 0) || 0
        const productDiscountsUnitAmount = -customerUnitSubtotal * productDiscountsYield / 100
        const productDiscountsUnitSubtotal = customerUnitSubtotal + productDiscountsUnitAmount
        
        const specialDiscountYield = -(item.specialDiscountYield || 0)
        const specialDiscountUnitAmount = productDiscountsUnitSubtotal * specialDiscountYield / 100
        const specialDiscountUnitSubtotal = productDiscountsUnitSubtotal + specialDiscountUnitAmount
        
        const paymentTypeAdjustmentUnitAmount = specialDiscountUnitSubtotal * paymentTypeYield / 100
        const paymentTypeUnitSubtotal = specialDiscountUnitSubtotal + paymentTypeAdjustmentUnitAmount

        const unitSubtotal = paymentTypeUnitSubtotal
        const unitVatAmount = unitSubtotal * vatTaxYield / 100
        const unitTotal = unitSubtotal + unitVatAmount
        
        const subtotal = price * quantity
        const customerAdjustmentAmount = customerAdjustmentUnitAmount * quantity
        const discountedAmount = (productDiscountsUnitAmount + specialDiscountUnitAmount) * quantity
        const paymentTypeAdjustmentAmount = paymentTypeAdjustmentUnitAmount * quantity
        const totalWithoutVAT = unitSubtotal * quantity
        const vatAmount = unitVatAmount * quantity
        const total = unitTotal * quantity

        return {
            productId,
            customerTypeYield,
            customerUnitSubtotal,
            productDiscountsUnitSubtotal,
            specialDiscountUnitSubtotal,
            paymentTypeUnitSubtotal,
            unitSubtotal,
            unitVatAmount,
            unitTotal,
            quantity,
            subtotal,
            customerAdjustmentAmount,
            discountedAmount,
            paymentTypeAdjustmentAmount,
            totalWithoutVAT,
            vatAmount,
            total
        }
    }

    const calculatePriceOutline = (
        items: Partial<Item>[],
        paymentTypeYield = 0,
        perceptions: Perception[] = []
    ): PriceOutlineType => {
        const itemsPriceOutline = items.map(item => calculateItemPriceOutline(item, item.customerTypeYield, item.vatTaxYield, paymentTypeYield))
        const subtotal = itemsPriceOutline.reduce((sum, item) => sum + item.subtotal, 0)
        const customerAdjustmentAmount = itemsPriceOutline.reduce((sum, item) => sum + item.customerAdjustmentAmount, 0)
        const discountedAmount = itemsPriceOutline.reduce((sum, item) => sum + item.discountedAmount, 0)
        const paymentTypeAdjustmentAmount = itemsPriceOutline.reduce((sum, item) => sum + item.paymentTypeAdjustmentAmount, 0)
        const totalWithoutVAT = itemsPriceOutline.reduce((sum, item) => sum + item.totalWithoutVAT, 0)
        const vatAmount = itemsPriceOutline.reduce((sum, item) => sum + item.vatAmount, 0)
        const perceptionsAmount = perceptions.reduce((sum, perception) => sum + perception.amount, 0)
        const total = itemsPriceOutline.reduce((sum, item) => sum + item.total, 0) + perceptionsAmount
        
        return {
            subtotal,
            customerAdjustmentAmount,
            discountedAmount,
            paymentTypeAdjustmentAmount,
            totalWithoutVAT,
            vatAmount,
            perceptionsAmount,
            total,
            items: itemsPriceOutline
        }
    }

    const getItemPriceOutlineLive = (
        item: Partial<Item>,
        customerTypeId?: string,
        paymentTypeId?: string
    ): ItemPriceBreakdownType => {
        const customerTypeYield = customerTypeId ? pricesHooks.product.customerTypeYield(customerTypeId!, item.productId!) : (
            isDefined(item.customerTypeYield) ? item.customerTypeYield! : 0
        )
        const vatTaxYield = isDefined(item.vatTaxYield) ? item.vatTaxYield! : pricesHooks.product.vatTaxYield(item.productId!)
        const paymentTypeYield = getPaymentTypeYield(paymentTypeId)
        return calculateItemPriceOutline(item, customerTypeYield, vatTaxYield, paymentTypeYield)
    }

    const getPriceOutlineLive = (
        items: Partial<Item>[],
        customerTypeId?: string,
        paymentTypeYield = 0,
        perceptions: Perception[] = []
    ): PriceOutlineType => {
        const itemsWithYields = items.map(item => {
            const customerTypeYield = customerTypeId ? pricesHooks.product.customerTypeYield(customerTypeId, item.productId!) : undefined
            return {
                ...item,
                customerTypeYield: item.customerTypeYield || customerTypeYield,
                // Solo se busca el IVA del producto si el ítem no lo tiene guardado
                vatTaxYield: item.vatTaxYield || pricesHooks.product.vatTaxYield(item.productId!)
            }
        })
        return calculatePriceOutline(itemsWithYields, paymentTypeYield, perceptions)
    }

    const getPriceOutlineLiveFromTotal = (
        total = 0,
        paymentTypeYield = 0,
        vatTaxYield = 0
    ): PriceOutlineType => {
        const totalWithoutVAT = total / (1 + (vatTaxYield / 100))
        const vatAmount = totalWithoutVAT * vatTaxYield / 100
        const subtotal = totalWithoutVAT / (1 + (paymentTypeYield / 100))
        const paymentTypeAdjustmentAmount = subtotal * paymentTypeYield / 100
        
        return {
            subtotal,
            customerAdjustmentAmount: 0,
            discountedAmount: 0,
            paymentTypeAdjustmentAmount,
            totalWithoutVAT,
            vatAmount,
            perceptionsAmount: 0,
            total,
            items: []
        }
    }

    const getPriceOutlineLiveFromSubtotal = (
        subtotal = 0,
        paymentTypeYield = 0,
        vatTaxYield = 0
    ): PriceOutlineType => {
        const paymentTypeAdjustmentAmount = subtotal * paymentTypeYield / 100
        const totalWithoutVAT = subtotal + paymentTypeAdjustmentAmount
        const vatAmount = totalWithoutVAT * vatTaxYield / 100
        const total = totalWithoutVAT + vatAmount
        
        return {
            subtotal,
            customerAdjustmentAmount: 0,
            discountedAmount: 0,
            paymentTypeAdjustmentAmount,
            totalWithoutVAT,
            vatAmount,
            perceptionsAmount: 0,
            total,
            items: []
        }
    }

    // El valor de un acopio financiero incluye IVA y excluye el ajuste del medio de pago
    const getPriceOutlineLiveFromHoardValue = (
        hoardValue = 0,
        paymentTypeYield = 0,
        vatTaxYield = 0
    ): PriceOutlineType => {
        const subtotal = hoardValue / (1 + (vatTaxYield / 100))
        return getPriceOutlineLiveFromSubtotal(subtotal, paymentTypeYield, vatTaxYield)
    }

    const getItemPriceOutline = (item: Item): ItemPriceBreakdownType => {
        return getItemPriceOutlineLive(item)
    }

    const getPriceOutline = (
        table: TableName,
        operation: Operation | Budget,
        paymentTypeId?: string
    ): PriceOutlineType => {
        if (table === TableName.PURCHASE_ORDERS) {
            return getPriceOutlineLive(operation.items)
        } else if (table === TableName.BUDGETS) {
            const budget = operation as Budget
            return getPriceOutlineLive(budget.items, budget.customerTypeId)
        } else if (table === TableName.HOARD_ORDERS) {
            const hoard = operation as HoardOrder
            const paymentTypeYield = paymentTypeId ? getPaymentTypeYield(paymentTypeId) : hoard.paymentTypeYield
            return hoard.type === HoardType.PRODUCT ?
                getPriceOutlineLive(hoard.items, undefined, paymentTypeYield, hoard.perceptions) :
                getPriceOutlineLiveFromHoardValue(hoard.value, paymentTypeYield, hoard.vatTaxYield)
        } else {
            const sale = operation as SaleOrder
            const paymentTypeYield = paymentTypeId ? getPaymentTypeYield(paymentTypeId) : sale.paymentTypeYield
            return getPriceOutlineLive(sale.items, undefined, paymentTypeYield, sale.perceptions)
        }
    }

    // What the customer owes for a sale/hoard: the operation total (never modified) minus its adjustment notes
    const getAdjustedTotal = (
        table: TableName,
        operation: Operation,
        paymentTypeId?: string
    ): number => {
        const adjustedAmount = (operation as SaleOrder | HoardOrder).adjustedAmount || 0
        return round(getPriceOutline(table, operation, paymentTypeId).total - adjustedAmount)
    }

    return {
        getItemPriceOutlineLive,
        getPriceOutlineLive,
        getPriceOutlineLiveFromTotal,
        getPriceOutlineLiveFromSubtotal,
        getPriceOutlineLiveFromHoardValue,
        getItemPriceOutline,
        getPriceOutline,
        getAdjustedTotal
    }
}
